import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axiosInstance from "../../../interceptors";
import { baseUrl } from "../../../config/config";

const blankQ = () => ({ type: "MCQ", question: "", options: ["", "", "", ""], correctAnswer: "", points: 10 });

export default function AssessmentBuilder() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const editId = params.get("id");

  const [form, setForm] = useState({
    title: "", description: "", assessmentType: "MCQ",
    timeLimitMinutes: 30, passingScore: 60, isPublished: false,
  });
  const [questions, setQuestions] = useState([blankQ()]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    document.title = "Assessment builder - Ample Hire";
    if (editId) {
      axiosInstance.get(`${baseUrl}/job-posting/assessments/${editId}`).then((r) => {
        const a = r.data;
        setForm({
          title: a.title || "", description: a.description || "",
          assessmentType: a.assessmentType || "MCQ",
          timeLimitMinutes: a.timeLimitMinutes || 30,
          passingScore: a.passingScore || 60, isPublished: !!a.isPublished,
        });
        let qs = a.questions;
        if (typeof qs === "string") { try { qs = JSON.parse(qs); } catch { qs = []; } }
        setQuestions(Array.isArray(qs) && qs.length ? qs : [blankQ()]);
      });
    }
  }, [editId]);

  const updateQ = (i, patch) => setQuestions((qs) => qs.map((q, idx) => idx === i ? { ...q, ...patch } : q));
  const updateOption = (i, j, val) => setQuestions((qs) => qs.map((q, idx) => idx === i ? { ...q, options: q.options.map((o, k) => k === j ? val : o) } : q));

  const save = async () => {
    if (!form.title.trim()) { alert("Title required"); return; }
    setBusy(true);
    try {
      const payload = { ...form, questions: JSON.stringify(questions) };
      if (editId) {
        await axiosInstance.put(`${baseUrl}/job-posting/assessments/${editId}`, payload);
      } else {
        await axiosInstance.post(`${baseUrl}/job-posting/assessments`, payload);
      }
      nav("/admin/assessments");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="page-enter">
      <div className="flex items-end justify-between mb-5">
        <div>
          <button onClick={() => nav(-1)} className="text-xs text-vf-muted hover:text-vf-base mb-1">← Back</button>
          <h1 className="text-2xl font-semibold text-vf-base">{editId ? "Edit assessment" : "New assessment"}</h1>
        </div>
        <button onClick={save} disabled={busy} className="px-4 py-2 rounded bg-vf-primary text-white text-sm">
          {busy ? "Saving…" : "Save assessment"}
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-5 mb-4 space-y-3">
        <input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} placeholder="Assessment title *" className="w-full px-3 py-2 border border-slate-300 rounded text-sm font-medium" />
        <textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} placeholder="Description (optional)" className="w-full px-3 py-2 border border-slate-300 rounded text-sm resize-none" rows={2} />
        <div className="grid grid-cols-3 gap-3">
          <label className="block">
            <div className="text-xs font-medium text-vf-muted mb-0.5">Time limit (min)</div>
            <input type="number" value={form.timeLimitMinutes} onChange={(e) => setForm({...form, timeLimitMinutes: Number(e.target.value)})} className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm" />
          </label>
          <label className="block">
            <div className="text-xs font-medium text-vf-muted mb-0.5">Passing score (%)</div>
            <input type="number" value={form.passingScore} onChange={(e) => setForm({...form, passingScore: Number(e.target.value)})} className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm" />
          </label>
          <label className="flex items-end gap-2 cursor-pointer">
            <input type="checkbox" checked={form.isPublished} onChange={(e) => setForm({...form, isPublished: e.target.checked})} />
            <span className="text-sm text-vf-base">Published</span>
          </label>
        </div>
      </div>

      <div className="space-y-3">
        {questions.map((q, i) => (
          <div key={i} className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs font-semibold text-vf-muted">Question {i + 1}</div>
              <button onClick={() => setQuestions(questions.filter((_, idx) => idx !== i))} className="text-xs text-red-600">Remove</button>
            </div>
            <input value={q.question} onChange={(e) => updateQ(i, { question: e.target.value })} placeholder="Question text" className="w-full px-3 py-2 border border-slate-300 rounded text-sm mb-2" />
            <div className="space-y-1">
              {q.options.map((opt, j) => (
                <div key={j} className="flex items-center gap-2">
                  <input
                    type="radio"
                    name={`correct-${i}`}
                    checked={q.correctAnswer === opt && opt !== ""}
                    onChange={() => updateQ(i, { correctAnswer: opt })}
                    title="Mark as correct"
                  />
                  <input
                    value={opt}
                    onChange={(e) => updateOption(i, j, e.target.value)}
                    placeholder={`Option ${String.fromCharCode(65 + j)}`}
                    className="flex-1 px-2 py-1 border border-slate-300 rounded text-sm"
                  />
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between mt-2">
              <label className="text-xs text-vf-muted">
                Points: <input type="number" value={q.points} onChange={(e) => updateQ(i, { points: Number(e.target.value) })} className="w-16 px-1 py-0.5 border border-slate-300 rounded text-xs ml-1" />
              </label>
              <span className="text-[10px] text-vf-muted">Click radio to mark the correct option.</span>
            </div>
          </div>
        ))}
        <button onClick={() => setQuestions([...questions, blankQ()])} className="w-full px-3 py-2 rounded border border-dashed border-slate-300 text-sm text-vf-muted hover:bg-slate-50">
          + Add question
        </button>
      </div>
    </div>
  );
}
